import { Injectable, inject } from '@angular/core';
import { IPOI, IPOICategory, POIService } from './poi.service';
import { ConjectFMService, IRoom } from './conjectfm.service';
import { Observable, map, switchMap, forkJoin, of } from 'rxjs';
import { findIconDefinition, library, parse } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { StudySpacesService } from '../../pages/home/study-areas/learning-spaces/study-spaces.service';

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private poiService = inject(POIService);
  private conjectFMService = inject(ConjectFMService);
  private studySpacesService = inject(StudySpacesService);


  constructor() {
    library.add(fas);
  }

  public geoJSONFeatureCollection(pois: IPOI[], categories: IPOICategory[] = []): GeoJSON.FeatureCollection {
    const featureCollection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: []
    }

    if(categories?.length !== 0) {
      for(const category of categories) {
        const categoryPOIs = pois.filter(poi => poi.categoryId === category.id);
        // All POIs that have a room / building attached to it are in this map (`buildingId` -> `IPOI[]`)
        const poisByBuildingId: { [key: number]: IPOI[] } = {};

        // POIs that does not have a room / building attached to it
        const otherPOIs: IPOI[] = []

        for(const poi of categoryPOIs) {
          if(poi.room) {
            if(poi.room.buildingId in poisByBuildingId) {
              poisByBuildingId[poi.room.buildingId].push(poi);
            } else {
              poisByBuildingId[poi.room.buildingId] = [poi];
            }
          } else {
            otherPOIs.push(poi);
          }
        }

        // Construct building pois as features
        for(const buildingId in poisByBuildingId) {
          let name = null;
          let coordinates = null;
          let roomDescriptions: { [key: number]: { de: String; en: String } } = {};
          let rooms: IRoom[] = [];
          for(const buildingPOI of poisByBuildingId[buildingId]) {
            coordinates = [buildingPOI.gps.longitude, buildingPOI.gps.latitude];

            if(!name) {
              name = buildingPOI.name;
            }

            if(buildingPOI.description) {
              roomDescriptions[buildingPOI.roomId] = buildingPOI.description;
            }
            rooms.push(buildingPOI.room);
          }

          const hasStudySpaces = poisByBuildingId[buildingId].some((poi) => poi.studySpace);

          const feature: GeoJSON.Feature = {
            id: buildingId,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates
            },
            properties: {
              id: buildingId,
              name,
              roomDescriptions,
              category,
              coordinates,
              icon: this.fontAwesomeCharacterCode(category.icon),
              rooms,
              hasStudySpaces
            }
          }
          featureCollection.features.push(feature);
        }

        for(const poi of otherPOIs) {
          const feature: GeoJSON.Feature = {
            id: poi.id,
            type: 'Feature',
            geometry: {
              type: 'Point',
              coordinates: [poi.gps.longitude, poi.gps.latitude]
            },
            properties: {
              id: poi.id,
              name: poi.name,
              description: poi.description,
              category,
              coordinates: [poi.gps.longitude, poi.gps.latitude],
              occupancyLevel: poi.occupancyLevel,
              icon: this.fontAwesomeCharacterCode(category.icon),
              hasStudySpaces: poi.studySpace
            }
          }
          featureCollection.features.push(feature);
        }
      }
    }
    return featureCollection;
  }

  /**
   * This function constructs a list of all Points of Interest with detailed room information from ConjectFM.
   *
   * @returns A list of IPOI's
   */
  public detailedPOIs(): Observable<IPOI[]> {
    return forkJoin([this.poiService.pois(), this.studySpacesService.getRoomsAsPOIs()]).pipe(
      map((poisLists: [IPOI[],IPOI[]]): IPOI[] => {
        for(const poi of poisLists[1]) {
          const poiOfList = poisLists[0].find((p1: IPOI): boolean => p1.id === poi.id && p1.categoryId === poi.categoryId)
          if(poiOfList) {
            poiOfList.studySpace = true;
          } else {
            poisLists[0].push(poi);
          }
          }
        return poisLists[0];
      }),
      switchMap((pois: IPOI[]) => {
        const roomIds = pois.filter(poi => !!poi.roomId && !poi.room).map(poi => poi.roomId);

        if (roomIds.length === 0) {
          return of(pois);
        }

        return this.conjectFMService.rooms(roomIds, true).pipe(
          map((rooms: IRoom[]): IPOI[] => {
            return pois.map(poi => {
              if (poi.roomId && !poi.room) {
                const room = rooms.find(room => room.id === poi.roomId);
                return { ...poi, room };
              }
              return poi;
            });
          })
        );
      }),
      map((pois) => {
        // Map POIs without a category to categoryId 0. This is safe since there will never be a category with the id 0 in the database.
        return pois.map(poi => {
          if (poi.categoryId === null) {
            return { ...poi, categoryId: 0 };
          }
          return poi;
        });
      })
    );
  }

  public fontAwesomeCharacterCode(iconName: string): string {
    const parseIcon = parse.icon(iconName);
    const icon = findIconDefinition(parseIcon);
    return String.fromCharCode(parseInt(icon.icon[3], 16));
  }
}
